import {isNil} from 'lodash';
import {Customer} from 'src/app/domain/model/customer/customer.model';
import {CustomerPortfolio, isCanceledPortfolio} from 'src/app/domain/model/customer-portfolio.model';
import {ProductId} from 'src/app/domain/model/product/product.model';
import {BackofficePortfolioAllocation} from 'src/app/domain/model/backoffice-portfolio-allocation.model';
import {BackofficePortfolioFee} from 'src/app/domain/model/backoffice-portfolio-fees.model';
import {InvestmentStrategy} from 'src/app/domain/model/investment-strategy/investment-strategy.model';

export type MoneyAggregationMethod = 'direct_debit' | 'bank_transfer';
export enum PerformanceType {
  tir = 'TIR',
  annualizedPerformance = 'annualized_performance',
}

export enum MaxPartialWithdrawalType {
  percentage = 'percentage',
  minValueAfterWithdrawal = 'min_value_after_withdrawal',
}

export const feeType = {
  management: 'management',
  custodian: 'custodian',
  funds: 'funds',
  other: 'other',
};

// TODO: Delete when the new graph is implemented. For now it seems that only the id and name are used.
export interface BenchmarkInfo {
  id: string;
  name: string;
  benchmarkVersusInternalRateOfReturn: number;
  benchmarkVersusPerformance: number;
  startDate: string; // "2021-11-18T12:45:30"
  endDate: string; // "2021-11-18T12:45:30"
}

export interface BackofficePortfolio {
  id: string;
  name: string;
  title: string;
  productId: ProductId;
  tolerance: number;
  externalTolerance: number;
  minAmount: number;
  equity: number;
  performance: number;
  netPerformance: number | null | undefined;
  performanceType: PerformanceType;
  contributionRetentionDays: number;
  maxPartialWithdrawalValue: number;
  maxPartialWithdrawalType: MaxPartialWithdrawalType;
  minInitialAmount: number;
  minChangePortfolioMaster: number;
  minFundsToBuy: number;
  calculatedMinFundsToBuy: number;
  fees: BackofficePortfolioFee[];
  allocations: BackofficePortfolioAllocation[];
  benchmark: BenchmarkInfo;
  moneyAggregationMethod: MoneyAggregationMethod;
  investmentStrategy: InvestmentStrategy;
  maxContracts: number | null;
}

export const getFeeByFilter = (backofficePortfolioFees: BackofficePortfolioFee[], value: number, type: string) => backofficePortfolioFees.find(
  (backofficePortfolioFee: BackofficePortfolioFee) =>
    backofficePortfolioFee.minAmount <= value &&
    backofficePortfolioFee.maxAmount > value &&
    backofficePortfolioFee.type === type
);

export const findRecommendedPortfolio = (
  portfolios: BackofficePortfolio[],
  product: ProductId,
  tolerance: number,
  investmentStrategy?: InvestmentStrategy,
): BackofficePortfolio | null => {
  const recommendedPortfolio = findRecommendedPortfolioMatch(portfolios, product, tolerance, investmentStrategy);
  return recommendedPortfolio || findRecommendedPortfolioMatch(portfolios, product, tolerance);
};

const findRecommendedPortfolioMatch = (
  boPortfolios: BackofficePortfolio[],
  productId: ProductId,
  maxTolerance: number,
  investmentStrategy?: InvestmentStrategy,
): BackofficePortfolio | null => {
  let recommendedPortfolio: BackofficePortfolio | null = null;

  boPortfolios.forEach((currentBoPortfolio) => {
    if(
      productId === currentBoPortfolio.productId
      && checkIfPortfolioInvestmentStrategyIsEqualToSelected(currentBoPortfolio, investmentStrategy)
      && maxTolerance >= currentBoPortfolio.tolerance
      && currentBoPortfolio.tolerance >= (recommendedPortfolio?.tolerance ?? 0)
    ) {
      recommendedPortfolio = currentBoPortfolio;
    }
  });

  return recommendedPortfolio;
};

const checkIfPortfolioInvestmentStrategyIsEqualToSelected = (
  boPortfolio: BackofficePortfolio,
  investmentStrategy?: InvestmentStrategy
): boolean => investmentStrategy
    ? investmentStrategy.id === boPortfolio.investmentStrategy.id
    : true;

export const boPortfoliosAvailableForCustomer = (customer: Customer, boPortfolios: BackofficePortfolio[]): BackofficePortfolio[] =>
  isNil(customer) ? [] : (boPortfolios ?? []).filter(boPortfolio => boPortfolio.tolerance <= customer.tolerance);

export const allocationsWithoutMultipleDistributors = (boPortfolio: BackofficePortfolio | null | undefined): BackofficePortfolioAllocation[] =>
  (boPortfolio?.allocations ?? []).filter(
    allocation => allocation.security?.allowsMultipleDistributors === false
  );

export const hasAllocationWithoutMultipleDistributors = (boPortfolio: BackofficePortfolio | null | undefined): boolean =>
  allocationsWithoutMultipleDistributors(boPortfolio).length > 0;

export const moneyAggregationMethodIsDirectDebit = (moneyAggregationMethod: MoneyAggregationMethod): boolean => moneyAggregationMethod === 'direct_debit';
export const boPortfolioFullTitle = (boPo: BackofficePortfolio): string => boPo?.externalTolerance + ' - ' + boPo?.title;

export const isPortfolioAtMaxContracts = (
  boPortfolio: BackofficePortfolio,
  customerPortfolios: CustomerPortfolio[]
): boolean => {
  if (isNil(boPortfolio.maxContracts)) {
    return false;
  }
  const contractCount = (customerPortfolios ?? []).filter(
    cp => !isCanceledPortfolio(cp) && cp.portfolioMaster === boPortfolio.id
  ).length;
  return contractCount >= boPortfolio.maxContracts;
};

export const filterPortfoliosNotAtMaxContracts = (
  boPortfolios: BackofficePortfolio[],
  customerPortfolios: CustomerPortfolio[]
): BackofficePortfolio[] =>
  boPortfolios.filter(bp => !isPortfolioAtMaxContracts(bp, customerPortfolios));

